// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Dialog for creating or editing a renderer definition.
 *
 * Slots are derived from the selected component's schema (via the
 * renderer-component registry). Admins bind each schema slot to a data
 * source — they do not invent slots. Column-only renderers (no component,
 * e.g. a risk badge) take a single dataset slot — the browser column reads
 * its data (scores, analysis status) from that binding.
 */
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import { Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAgentDatasets } from '@/subsystems/pipelines/hooks/useDatasets'
import {
  getRendererComponent,
  listRendererComponentsByGroup,
  type SlotSchema,
} from '@/app-shell/renderer-components'
import type { RendererDefinition, RendererSlot } from '@/app-shell/types/workspace'

export interface RendererEditData {
  key: string
  label: string
  component: string | null
  slots: RendererSlot[]
  columnType?: 'score' | 'analysis' | 'risk' | null
  order?: number
}

interface RendererEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  renderer?: RendererDefinition | null
  onSave: (data: RendererEditData) => void
  saving?: boolean
  /** Org/ws/agent for listing the agent's datasets in the slot dropdown. */
  orgId?: string
  workspaceId?: string
  agentId?: string
}

const NONE_COMPONENT = '__none__'

/** Default slot key for the dataset binding of a column-only renderer. */
const COLUMN_ONLY_SLOT_KEY = 'data'

export const RendererEditDialog: FC<RendererEditDialogProps> = ({
  open,
  onOpenChange,
  renderer,
  onSave,
  saving,
  orgId,
  workspaceId,
  agentId,
}) => {
  const isNew = !renderer
  const [key, setKey] = useState('')
  const [label, setLabel] = useState('')
  const [component, setComponent] = useState<string>('')
  const [slots, setSlots] = useState<RendererSlot[]>([])
  const [columnType, setColumnType] = useState<'score' | 'analysis' | 'risk' | ''>('')
  const [order, setOrder] = useState<number>(0)

  const { datasets, loading: datasetsLoading } = useAgentDatasets(orgId, workspaceId, agentId)

  useEffect(() => {
    if (open && renderer) {
      setKey(renderer.key)
      setLabel(renderer.label)
      setComponent(renderer.component ?? '')
      setSlots(renderer.slots.length > 0 ? [...renderer.slots] : [])
      setColumnType(renderer.columnType ?? '')
      setOrder(renderer.order ?? 0)
    } else if (open && !renderer) {
      setKey('')
      setLabel('')
      setComponent('')
      setSlots([])
      setColumnType('')
      setOrder(0)
    }
  }, [open, renderer])

  const componentDef = useMemo(
    () => (component ? getRendererComponent(component) : null),
    [component],
  )
  const componentGroups = useMemo(() => listRendererComponentsByGroup(), [])
  // Column-only renderers (no component) still take one dataset slot — the
  // browser column reads its data (e.g. risk scores) from that binding.
  const schemaSlots = useMemo<SlotSchema[]>(() => {
    if (component) return componentDef?.slots ?? []
    return [
      {
        key: slots.find(s => s.type === 'dataset')?.key ?? COLUMN_ONLY_SLOT_KEY,
        type: 'dataset',
        required: true,
        label: 'Data source',
        description: 'Dataset the browser column reads (e.g. risk scores).',
      },
    ]
  }, [component, componentDef, slots])
  const schemaSlotKeys = useMemo(
    () => new Set(schemaSlots.map(s => s.key)),
    [schemaSlots],
  )
  const unrecognizedSlots = slots.filter(s => !schemaSlotKeys.has(s.key))

  const findBinding = useCallback(
    (slotKey: string) => slots.find(s => s.key === slotKey),
    [slots],
  )

  const upsertSchemaBinding = useCallback(
    (schema: SlotSchema, patch: Partial<RendererSlot>) => {
      setSlots(prev => {
        const idx = prev.findIndex(s => s.key === schema.key)
        const next: RendererSlot = {
          key: schema.key,
          type: schema.type,
          ...(schema.type === 'document_text' && schema.multiVersion
            ? { multiVersion: true }
            : {}),
          ...(idx >= 0 ? prev[idx] : {}),
          ...patch,
        }
        if (idx >= 0) return prev.map((s, i) => (i === idx ? next : s))
        return [...prev, next]
      })
    },
    [],
  )

  const removeSlot = useCallback((slotKey: string) => {
    setSlots(prev => prev.filter(s => s.key !== slotKey))
  }, [])

  const requiredUnbound = schemaSlots.some(
    s =>
      s.required &&
      s.type === 'dataset' &&
      !(findBinding(s.key)?.datasetKey ?? '').trim(),
  )

  const canSave =
    Boolean(key.trim()) && Boolean(label.trim()) && !requiredUnbound && !saving

  const handleSave = useCallback(() => {
    if (!canSave) return
    // Normalize: enforce schema type for matched slots, drop empty bindings.
    const normalized: RendererSlot[] = []
    for (const schema of schemaSlots) {
      const binding = findBinding(schema.key)
      if (schema.type === 'dataset') {
        const datasetKey = binding?.datasetKey?.trim() ?? ''
        if (!datasetKey && !schema.required) continue
        normalized.push({ key: schema.key, type: 'dataset', datasetKey })
      } else {
        normalized.push({
          key: schema.key,
          type: 'document_text',
          multiVersion: Boolean(schema.multiVersion),
        })
      }
    }
    // Preserve unrecognized slots so admins can clean them up explicitly.
    for (const slot of unrecognizedSlots) {
      normalized.push(slot)
    }
    onSave({
      key: key.trim(),
      label: label.trim(),
      component: component || null,
      slots: normalized,
      columnType: columnType || null,
      order,
    })
  }, [
    canSave,
    schemaSlots,
    findBinding,
    unrecognizedSlots,
    onSave,
    key,
    label,
    component,
    columnType,
    order,
  ])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Renderer' : `Edit ${renderer?.label}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="rnd-key">Key</Label>
            <Input
              id="rnd-key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="findings-renderer"
              disabled={!isNew}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rnd-label">Label</Label>
            <Input
              id="rnd-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Findings"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rnd-component">Component</Label>
            <Select
              value={component || NONE_COMPONENT}
              onValueChange={(v) => setComponent(v === NONE_COMPONENT ? '' : v)}
            >
              <SelectTrigger id="rnd-component">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_COMPONENT}>None (column only)</SelectItem>
                {componentGroups.map(({ group, components }) => (
                  <SelectGroup key={group}>
                    <SelectLabel>{group}</SelectLabel>
                    {components.map(def => (
                      <SelectItem key={def.id} value={def.id}>
                        {def.label}
                        <span className="text-muted-foreground"> ({def.id})</span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
                {component && !getRendererComponent(component) && (
                  <SelectItem value={component}>
                    {component}
                    <span className="text-muted-foreground"> (unknown)</span>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {component && !componentDef && (
              <p className="text-xs text-amber-600">
                Component <code>{component}</code> isn't registered. Slot bindings can't be edited safely.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rnd-column-type">Column Type</Label>
            <Select value={columnType || 'none'} onValueChange={(v) => setColumnType(v === 'none' ? '' : v as 'score' | 'analysis' | 'risk')}>
              <SelectTrigger id="rnd-column-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="score">Score</SelectItem>
                <SelectItem value="analysis">Analysis</SelectItem>
                <SelectItem value="risk">Risk</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <SchemaSlotsSection
            schemaSlots={schemaSlots}
            componentDef={componentDef}
            component={component}
            findBinding={findBinding}
            upsertSchemaBinding={upsertSchemaBinding}
            datasets={datasets}
            datasetsLoading={datasetsLoading}
          />

          {unrecognizedSlots.length > 0 && (
            <UnrecognizedSlotsSection
              slots={unrecognizedSlots}
              onRemove={removeSlot}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface SchemaSlotsSectionProps {
  schemaSlots: SlotSchema[]
  componentDef: ReturnType<typeof getRendererComponent>
  component: string
  findBinding: (slotKey: string) => RendererSlot | undefined
  upsertSchemaBinding: (schema: SlotSchema, patch: Partial<RendererSlot>) => void
  datasets: Array<{ key: string; label: string }>
  datasetsLoading: boolean
}

const SchemaSlotsSection: FC<SchemaSlotsSectionProps> = ({
  schemaSlots,
  componentDef,
  component,
  findBinding,
  upsertSchemaBinding,
  datasets,
  datasetsLoading,
}) => {
  if (component && !componentDef) {
    return null
  }
  if (componentDef && schemaSlots.length === 0) {
    return (
      <div className="space-y-2">
        <Label>Slots</Label>
        <p className="text-xs text-muted-foreground">
          {componentDef.label} takes no slots.
        </p>
      </div>
    )
  }
  return (
    <div className="space-y-2">
      <Label>Slots</Label>
      {schemaSlots.map(schema => {
        const binding = findBinding(schema.key)
        return (
          <div key={schema.key} className="border rounded-md p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm">{schema.key}</span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {schema.type}
              </span>
              {schema.required && (
                <span className="text-[10px] uppercase tracking-wide text-destructive">
                  required
                </span>
              )}
              {schema.label && (
                <span className="text-xs text-muted-foreground">— {schema.label}</span>
              )}
            </div>
            {schema.description && (
              <p className="text-xs text-muted-foreground">{schema.description}</p>
            )}
            {schema.type === 'dataset' && (
              <Select
                value={binding?.datasetKey || ''}
                onValueChange={(v) => upsertSchemaBinding(schema, { datasetKey: v })}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder={datasetsLoading ? 'Loading...' : 'Select a dataset...'} />
                </SelectTrigger>
                <SelectContent>
                  {datasets.map((ds) => (
                    <SelectItem key={ds.key} value={ds.key}>
                      {ds.label}
                      <span className="text-muted-foreground"> ({ds.key})</span>
                    </SelectItem>
                  ))}
                  {binding?.datasetKey && !datasets.some((ds) => ds.key === binding.datasetKey) && (
                    <SelectItem value={binding.datasetKey}>
                      {binding.datasetKey}
                      <span className="text-muted-foreground"> (missing)</span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
            {schema.type === 'document_text' && (
              <p className="text-xs text-muted-foreground">
                {schema.multiVersion
                  ? 'Multi-version (compares two document versions).'
                  : 'Single-version document text.'}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}

interface UnrecognizedSlotsSectionProps {
  slots: RendererSlot[]
  onRemove: (slotKey: string) => void
}

const UnrecognizedSlotsSection: FC<UnrecognizedSlotsSectionProps> = ({ slots, onRemove }) => (
  <div className="space-y-2 border-l-2 border-amber-300 pl-3">
    <div>
      <Label className="text-amber-700">Unrecognized slots</Label>
      <p className="text-xs text-muted-foreground">
        These slots aren't declared by the selected component's schema. They
        will be saved as-is until removed.
      </p>
    </div>
    {slots.map(slot => (
      <div
        key={slot.key}
        className="flex items-center gap-2 text-sm border rounded-md px-3 py-2"
      >
        <span className="font-mono">{slot.key}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {slot.type}
        </span>
        {slot.datasetKey && (
          <span className="text-xs text-muted-foreground truncate flex-1">
            → {slot.datasetKey}
          </span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive shrink-0"
          onClick={() => onRemove(slot.key)}
          aria-label={`Remove slot ${slot.key}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    ))}
  </div>
)
